/**
 * Choosing what people actually see.
 *
 * A picture used to be uploaded exactly as it came off the disk and then
 * cropped by CSS wherever it landed - object-cover, centred, take it or
 * leave it. A banner shot on a phone became a band across its own middle,
 * and the one part of it worth showing was off both edges.
 *
 * So this sits between the file picker and the upload: drag the picture to
 * move it, drag the slider to zoom, and what is inside the frame is what
 * gets sent. A banner is asked twice - once wide for a desktop and once tall
 * for a phone - because those are genuinely different pictures and picking
 * one crop for both is how a banner ends up with somebody's forehead on it.
 *
 * The crop happens here rather than on the server: the canvas is already in
 * the page, the result is smaller than the original so the upload is faster,
 * and the server never has to hold a 12MB phone photo to make a 400px
 * avatar out of it.
 *
 * A GIF is passed through untouched. Drawing one to a canvas would keep the
 * first frame and throw the animation away, and an animated picture is the
 * whole point of having uploaded one.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Button, Modal } from './ui';

export type Shape = 'avatar' | 'banner' | 'banner-tall';

type Spec = { label: string; note: string; aspect: number; out: [number, number] };

/**
 * What each crop is for, and what comes out of it.
 *
 * The output sizes are what the app actually draws at, doubled for a retina
 * screen and no further: an avatar is never bigger than 150px in this app,
 * so sending 1024 is three quarters of a megabyte nobody looks at.
 */
const SPECS: Record<Shape, Spec> = {
  avatar: { label: 'Profile picture', note: 'Shown as a rounded square everywhere in Pugo.', aspect: 1, out: [400, 400] },
  banner: { label: 'Banner on a computer', note: 'Wide. This is the one most people will see.', aspect: 1000 / 300, out: [1400, 420] },
  'banner-tall': { label: 'Banner on a phone', note: 'Taller, because a phone shows less of the width.', aspect: 900 / 500, out: [900, 500] }
};

type Placed = { scale: number; x: number; y: number };

export function CropPicture({ file, shapes, onDone, onCancel, busy }: {
  file: File;
  /** Which crops to ask for, in order. */
  shapes: Shape[];
  /** One blob per shape, in the same order. */
  onDone: (out: { shape: Shape; blob: Blob }[]) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState<{ shape: Shape; blob: Blob }[]>([]);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [place, setPlace] = useState<Placed>({ scale: 1, x: 0, y: 0 });
  const box = useRef<HTMLDivElement>(null);
  const url = useRef<string>('');

  const shape = shapes[step]!;
  const spec = SPECS[shape];
  /* An animated picture goes up as it is - see the note at the top. */
  const animated = /gif/i.test(file.type);

  useEffect(() => {
    url.current = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => setImg(el);
    el.src = url.current;
    return () => URL.revokeObjectURL(url.current);
  }, [file]);

  /* Each step starts with the picture filling the frame and centred, which
     is the crop somebody would have got before and is right often enough. */
  useEffect(() => { setPlace({ scale: 1, x: 0, y: 0 }); }, [step]);

  /* ---- moving it ---- */

  const drag = useRef<{ px: number; py: number; from: Placed } | null>(null);

  const onDown = (e: React.PointerEvent) => {
    if (e.button !== 0 || !img) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, from: place };
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const frame = box.current;
    if (!d || !frame || !img) return;
    const r = frame.getBoundingClientRect();
    setPlace(clamp({ ...d.from, x: d.from.x + (e.clientX - d.px) / r.width, y: d.from.y + (e.clientY - d.py) / r.height }, img, spec.aspect));
  };
  const onUp = () => { drag.current = null; };

  /* ---- what the frame shows, as a transform on the picture ---- */

  const cover = img ? coverScale(img, spec.aspect) : { w: 1, h: 1 };
  /*
   * Whether there is anything to drag.
   *
   * At 1x a picture the same shape as the frame fills it exactly, so there
   * is no slack and dragging correctly does nothing - which reads as broken
   * unless the page says so. Zooming in makes slack; this is how the hint
   * below knows which sentence to show.
   */
  const canMove = !animated && !!img && (cover.w * place.scale > 1.001 || cover.h * place.scale > 1.001);

  const next = useCallback(async () => {
    if (!img) return;
    const blob = animated
      ? file
      : await cut(img, spec, place);
    const all = [...done, { shape, blob }];
    if (step + 1 < shapes.length) { setDone(all); setStep(step + 1); return; }
    onDone(all);
  }, [img, animated, file, spec, place, done, shape, step, shapes.length, onDone]);

  return (
    <Modal
      title={spec.label}
      subtitle={animated ? 'Animated pictures are sent as they are, so the animation is kept.' : spec.note}
      onClose={onCancel}
      footer={
        <>
          <span className="flex-1 text-[12.5px] text-mute">
            {shapes.length > 1 ? 'Step ' + (step + 1) + ' of ' + shapes.length : ''}
          </span>
          <Button kind="plain" onClick={onCancel}>Cancel</Button>
          <Button kind="primary" disabled={!img || busy} onClick={() => void next()}>
            {busy ? 'Saving…' : step + 1 < shapes.length ? 'Next' : 'Use this'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* The frame. What is inside it is what gets sent - so the picture
            moves under a fixed window rather than the window moving. */}
        {/*
          A window of a fixed size.

          The frame used to take whatever height its aspect ratio asked for
          at full width, so a square crop opened a dialog as tall as the
          screen. It is capped now, and the picture moves behind it: the
          window is the thing that stays still.
        */}
        <div
          ref={box}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          /*
           * Capped by height, and the width follows from the ratio.
           *
           * max-height alone does not hold a ratio: the box took the full
           * width, hit the cap, and came out 490x340 for a crop that is
           * square - so the guides were drawn over an area that was not the
           * area being cut. Bounding both sides keeps the frame exactly the
           * shape of the result.
           */
          style={{
            aspectRatio: String(spec.aspect),
            maxHeight: 'min(52vh, 340px)',
            maxWidth: 'calc(min(52vh, 340px) * ' + spec.aspect + ')'
          }}
          className={cn(
            'relative mx-auto w-full touch-none overflow-hidden bg-[repeating-conic-gradient(var(--bg-2)_0_25%,var(--bg-3)_0_50%)] bg-[length:18px_18px]',
            'select-none',
            canMove ? 'cursor-grab active:cursor-grabbing' : 'cursor-default',
            shape === 'avatar' ? 'rounded-[26%]' : 'rounded-[var(--radius)]'
          )}
        >
          {img ? (
            <img
              src={url.current}
              alt=""
              draggable={false}
              style={animated ? { width: '100%', height: '100%', objectFit: 'cover' } : {
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: cover.w * 100 * place.scale + '%',
                height: cover.h * 100 * place.scale + '%',
                transform: 'translate(-50%, -50%) translate(' + place.x * 100 + '%, ' + place.y * 100 + '%)'
              }}
            />
          ) : (
            <span className="grid h-full place-items-center text-[13px] text-mute">Opening…</span>
          )}

          {/*
            What people actually see.

            Thirds, so there is something to line a face up against, and -
            on a banner - the band the name and picture sit over on a real
            profile, which is the part nobody will be able to read anything
            in. Drawn over the picture and never in the crop: this is the
            frame talking, not the image.
          */}
          {img && !animated ? (
            <span aria-hidden className="pointer-events-none absolute inset-0">
              <span className="absolute inset-y-0 left-1/3 w-px bg-white/18" />
              <span className="absolute inset-y-0 left-2/3 w-px bg-white/18" />
              <span className="absolute inset-x-0 top-1/3 h-px bg-white/18" />
              <span className="absolute inset-x-0 top-2/3 h-px bg-white/18" />
              {shape === 'avatar' ? (
                /* The rounded square everything in Pugo draws an avatar in.
                   Anything outside it is cropped again by the app. */
                <span className="absolute inset-0 rounded-[26%] shadow-[0_0_0_1px_rgba(255,255,255,.5),0_0_0_9999px_rgba(0,0,0,.35)]" />
              ) : (
                <span className="absolute inset-x-0 bottom-0 h-[38%] border-t border-dashed border-white/35 bg-[linear-gradient(180deg,transparent,rgba(0,0,0,.34))]">
                  <span className="absolute bottom-1 left-2 text-[10px] font-semibold tracking-[.04em] text-white/70 uppercase">
                    your name sits here
                  </span>
                </span>
              )}
            </span>
          ) : null}
        </div>

        {animated ? (
          <p className="m-0 flex items-center gap-2 text-[12.5px] text-mute">
            <Icon name="alert" size={14} className="flex-none text-warn" />
            A GIF cannot be cropped without losing the animation, so this one goes up whole.
          </p>
        ) : (
          <label className="flex items-center gap-3">
            <Icon name="search" size={15} className="flex-none text-mute" />
            <input
              type="range"
              min={1}
              max={4}
              step={0.01}
              value={place.scale}
              disabled={!img}
              aria-label="Zoom"
              onChange={(e) => img && setPlace((p) => clamp({ ...p, scale: Number(e.target.value) }, img, spec.aspect))}
              className="h-1.5 min-w-0 flex-1 accent-[var(--accent)]"
            />
            <span className="w-[42px] flex-none text-right text-[12px] tabular-nums text-mute">
              {place.scale.toFixed(1)}×
            </span>
          </label>
        )}

        <p className="m-0 text-[12px] text-ghost">
          {animated
            ? 'Drag is off for animated pictures.'
            : canMove
              ? 'Drag the picture to move it. What is inside the frame is what people see.'
              : 'Zoom in to move the picture around. What is inside the frame is what people see.'}
        </p>
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------- maths */

/** How much bigger than the frame the picture is when it just covers it. */
function coverScale(img: HTMLImageElement, aspect: number) {
  const imgAspect = img.naturalWidth / img.naturalHeight;
  return imgAspect > aspect
    ? { w: imgAspect / aspect, h: 1 }   // wider than the frame: full height
    : { w: 1, h: aspect / imgAspect };  // taller: full width
}

/**
 * Keep the frame covered.
 *
 * Without this the picture can be dragged until a corner of the frame is
 * empty, and the empty corner is then baked into what everybody sees.
 */
function clamp(p: Placed, img: HTMLImageElement, aspect: number): Placed {
  const scale = Math.min(4, Math.max(1, p.scale));
  const cover = coverScale(img, aspect);
  /* How much of the picture hangs outside the frame, as a fraction of the
     frame, on each side. */
  const slackX = Math.max(0, (cover.w * scale - 1) / 2);
  const slackY = Math.max(0, (cover.h * scale - 1) / 2);
  return {
    scale,
    x: Math.min(slackX, Math.max(-slackX, p.x)),
    y: Math.min(slackY, Math.max(-slackY, p.y))
  };
}

/** Draw exactly what the frame is showing, at the size the app draws it. */
function cut(img: HTMLImageElement, spec: Spec, p: Placed): Promise<Blob> {
  const [w, h] = spec.out;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('No canvas'));
  ctx.imageSmoothingQuality = 'high';

  const cover = coverScale(img, spec.aspect);
  const drawW = w * cover.w * p.scale;
  const drawH = h * cover.h * p.scale;
  const left = (w - drawW) / 2 + p.x * w;
  const top = (h - drawH) / 2 + p.y * h;
  ctx.drawImage(img, left, top, drawW, drawH);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not read that picture'))),
      'image/webp',
      0.92
    );
  });
}
